import {
  EgerdauCustomer,
  useSaveNestedFileMutation,
  useSaveUncombinedFileMutation,
  useSendFileToNestingServiceMutation,
  useEgerdauToken,
  toggleUnnestedAPINewEndpoint,
  getErrorMessageByCode,
  isNetworkFailure,
  ErrorMap,
  ErrorType,
} from "common/services";
import { FileType, FileUpload } from "features/Quote/Upload";
import { useCallback, useEffect, useState } from "react";
import { appState, useAppSelector, useAppDispatch } from "app";
import { showError } from "app/slice";

interface FileUploadResult {
  uid: string | null;
  quoteId: number | null;
  nestingUid: string | null;
  token: string | null;
}
const hasFileChanged = (current: FileUpload, prev: FileUpload) =>
  current.fileType !== prev.fileType ||
  current.file?.name !== prev.file?.name ||
  current.area !== prev.area;
const getInitResult = (): FileUploadResult => ({
  quoteId: null,
  uid: null,
  nestingUid: null,
  token: null,
});

export const useSaveUploadedFile = (file: FileUpload, customer: EgerdauCustomer | null) => {
  const dispatch = useAppDispatch();
  const egerdauToken = useEgerdauToken();
  const { sapAuxProfile } = useAppSelector(appState);
  const [cachedFile, setcachedFile] = useState<FileUpload>(file);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<any>(null);
  const [result, setResult] = useState<FileUploadResult>(getInitResult());
  const [triggerUpload, setTriggerUpload] = useState<boolean>(false);
  const [postFileNestingService] = useSendFileToNestingServiceMutation();
  const [postUncombinedQQTApi] = useSaveUncombinedFileMutation();
  const [postNestedQQTApi] = useSaveNestedFileMutation();
  const resetError = () => {
    setError(null);
    setcachedFile({ file: null, fileType: null, area: null });
  };
  const resetLastResult = () => setResult(getInitResult());
  const uploadComplete = (result: FileUploadResult) => {
    setResult(result);
    setError(null);
  };

  const handleUploadError = useCallback(
    (error: any) => {
      console.log("Raw error object:", error);
      setError(error);

      // Network failure (server unreachable, offline, DNS, etc.) — show
      // the friendlier "Connection Problem" message rather than a generic
      // service error so the user knows what to try.
      if (isNetworkFailure(error)) {
        const networkMsg = ErrorMap.get(ErrorType.NetworkError)!;
        console.log("Detected network failure, using NetworkError message");
        dispatch(
          showError({
            title: networkMsg.title,
            message: networkMsg.message,
            critical: false,
          }),
        );
        return;
      }

      // Check if there's a fieldError in the response for more specific handling
      if (error?.data?.fieldError) {
        const { rowNumber, fieldName, fieldValue, constraintMessage } = error.data.fieldError;
        const customMessage = (
          <>
            <p>Validation error at row {rowNumber}:</p>
            <p>
              Field <strong>'{fieldName}'</strong> with value <strong>'{fieldValue}'</strong> failed
              validation.
            </p>
            <p>{constraintMessage}</p>
            <p className="mt-3">Please correct the error in your file and try uploading again.</p>
          </>
        );

        console.log("Using custom field error message");
        dispatch(
          showError({
            title: "File Validation Error",
            message: customMessage,
            critical: false,
          }),
        );
      } else if (error?.data?.error) {
        const errorMessage = getErrorMessageByCode(error.data.error);
        console.log("Using mapped error message for code:", error.data.error, errorMessage);
        dispatch(
          showError({
            title: errorMessage.title,
            message: errorMessage.message,
            critical: false,
          }),
        );
      } else if (error?.data?.message || error?.data?.originalMessage) {
        const backendMessage = error.data.originalMessage || error.data.message;
        console.log("Using backend message:", backendMessage);
        dispatch(
          showError({
            title: "File Upload Error",
            message: backendMessage,
            critical: false,
          }),
        );
      } else {
        console.error("Upload error - no backend message found:", error);
        dispatch(
          showError({
            title: "Service Error",
            message:
              "We encountered an internal error. Our service may be down, Please try again later.",
            critical: false,
          }),
        );
      }
    },
    [dispatch],
  );

  if (file.file && hasFileChanged(file, cachedFile) && !error && !isLoading) {
    setcachedFile(file);
    setTriggerUpload(true);
  }

  useEffect(() => {
    const postUncombinedFile = async () => {
      const result = getInitResult();
      const { file: localFile, area } = file;
      const isIntelligentUpload = area === "intelligent";

      try {
        if (localFile) {
          setIsLoading(true);
          console.log("Uploading uncombined file with intelligent upload:", isIntelligentUpload);

          const body = new FormData();
          body.append("file", localFile);
          if (!toggleUnnestedAPINewEndpoint) {
            result.nestingUid = await postFileNestingService({
              args: { body, token: egerdauToken },
              token: egerdauToken,
            }).unwrap();
          }
          if (result.nestingUid || toggleUnnestedAPINewEndpoint) {
            const formData = new FormData();
            formData.append("upload_file", localFile);
            formData.append("user_info", JSON.stringify(customer));
            formData.append("customer_data", JSON.stringify(sapAuxProfile));

            console.log("FormData contents:", {
              upload_file: localFile.name,
              isIntelligentUpload: isIntelligentUpload,
              area: area,
            });

            const uploadResult = await postUncombinedQQTApi({
              body: formData,
              token: egerdauToken,
              isIntelligentUpload: isIntelligentUpload,
            }).unwrap();
            result.uid = uploadResult.uid;
            result.quoteId = uploadResult.quoteId;
          }
          uploadComplete(result);
        }
      } catch (error) {
        handleUploadError(error);
      } finally {
        setIsLoading(false);
        setTriggerUpload(false);
      }
    };
    if (triggerUpload && file.fileType === FileType.Unnested) {
      postUncombinedFile();
    }
  }, [
    sapAuxProfile,
    customer,
    file,
    postFileNestingService,
    postUncombinedQQTApi,
    triggerUpload,
    egerdauToken,
    handleUploadError,
  ]);

  useEffect(() => {
    const postNestedFile = async () => {
      const result: FileUploadResult = getInitResult();
      const { file: localFile, area } = file;
      const isIntelligentUpload = area === "intelligent";

      try {
        if (localFile) {
          setIsLoading(true);
          console.log("Uploading nested file with intelligent upload:", isIntelligentUpload);

          const formData = new FormData();
          formData.append("upload_file", localFile);
          formData.append("user_info", JSON.stringify(customer));
          formData.append("customer_data", JSON.stringify(sapAuxProfile));

          console.log("FormData contents:", {
            upload_file: localFile.name,
            isIntelligentUpload: isIntelligentUpload,
            area: area,
          });

          const uploadResult = await postNestedQQTApi({
            body: formData,
            token: egerdauToken,
            isIntelligentUpload: isIntelligentUpload,
          }).unwrap();
          result.uid = uploadResult.uid;
          result.quoteId = uploadResult.quoteId;
          uploadComplete(result);
        }
      } catch (error) {
        handleUploadError(error);
      } finally {
        setIsLoading(false);
        setTriggerUpload(false);
      }
    };
    if (triggerUpload && file.fileType === FileType.Nested) {
      postNestedFile();
    }
  }, [
    sapAuxProfile,
    customer,
    file,
    postNestedQQTApi,
    triggerUpload,
    egerdauToken,
    handleUploadError,
  ]);
  return { ...result, error, isLoading, resetError, resetLastResult };
};
